import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { API_PATH } from 'src/environments/environment';
import { map, catchError, of, switchMap } from 'rxjs';
import { AccountsDashboardFilter, SummaryResponse } from 'src/app/domain/registration/interfaces/accounts-payable-summary-dashboard';
import { fi } from 'date-fns/locale';

@Injectable({
  providedIn: 'root'
})
export class PartnerDashboardService {
  private readonly basePath = 'partners-dashboard';

  constructor(private httpClient: HttpClient) { }

  public getPartnerDashboard(filters: any): Observable<any> {
    return this.httpClient.post<any>(`${API_PATH}${this.basePath}/partner-dashboard`, filters);
  }

  public getPartnersSummary(): Observable<any> {
    return this.httpClient.get<any>(`${API_PATH}${this.basePath}/partners-summary`);
  }

  public getMonthlyRevenue(partnerId?: string, dateStart?: string, dateEnd?: string): Observable<any> {
    let url = `${API_PATH}${this.basePath}/monthly-revenue`;
    const params: any = {};

    if (partnerId) params.partnerId = partnerId;
    if (dateStart) params.dateStart = dateStart;
    if (dateEnd) params.dateEnd = dateEnd;

    return this.httpClient.get<any>(url, { params });
  }

  public getSystemsSummary(partnerId?: string): Observable<any> {
    let url = `${API_PATH}${this.basePath}/systems-summary`;
    const params: any = {};

    if (partnerId) params.partnerId = partnerId;

    return this.httpClient.get<any>(url, { params });
  }

  /**
   * Busca os sistemas vinculados ao parceiro com base nos planos
   * @param partnerId ID do parceiro
   * @returns Observable com os dados dos sistemas
   */
  public getPartnerSystems(partnerId?: string): Observable<any> {
    let url = `${API_PATH}${this.basePath}/partner-systems`;
    const params: any = {};

    if (partnerId) params.partnerId = partnerId;

    return this.httpClient.get<any>(url, { params });
  }

  /**
   * Busca o faturamento mensal do parceiro
   * @param partnerId ID do parceiro
   * @param dateStart Data inicial
   * @param dateEnd Data final
   * @returns Observable com os dados de faturamento
   */
  public getPartnerMonthlyBilling(partnerId?: string, dateStart?: string, dateEnd?: string): Observable<any> {
    let url = `${API_PATH}${this.basePath}/partner-monthly-billing`;
    const params: any = {};

    if (partnerId) params.partnerId = partnerId;
    if (dateStart) params.dateStart = dateStart;
    if (dateEnd) params.dateEnd = dateEnd;

    return this.httpClient.get<any>(url, { params });
  }

  /**
   * Busca o total de clientes ativos, inativos e bloqueados do parceiro
   * @param partnerId ID do parceiro
   * @returns Observable com os dados dos clientes
   */
  public getCustomerCounts(partnerId?: string): Observable<{ total: number, ativos: number, inativos: number, bloqueados: number }> {
    let url = `${API_PATH}${this.basePath}/customer-counts`;
    const params: any = {};

    if (partnerId) params.partnerId = partnerId;

    return this.httpClient.get<{ total: number, ativos: number, inativos: number, bloqueados: number }>(url, { params });
  }

  /**
   * Busca o faturamento por período (dia, mês, ano)
   * @param period Período de agrupamento (day, month, year)
   * @param partnerId ID do parceiro
   * @param dateStart Data inicial
   * @param dateEnd Data final
   * @returns Observable com os dados de faturamento agrupados pelo período
   */
  public getBillingByPeriod(
    period: string = 'month',
    partnerId?: string,
    dateStart?: string,
    dateEnd?: string
  ): Observable<any> {
    let url = `${API_PATH}${this.basePath}/billing-by-period`;
    const params: any = { period };

    if (partnerId) params.partnerId = partnerId;
    if (dateStart) params.dateStart = dateStart;
    if (dateEnd) params.dateEnd = dateEnd;

    return this.httpClient.get<any>(url, { params });
  }
}

@Injectable({
  providedIn: 'root'
})
export class AccountDashboardService {
  private readonly basePath = 'accounts-dashboard';

  constructor(private httpClient: HttpClient) { }


  /**
   * Obtém o resumo detalhado das contas a receber, incluindo dados para modais
   * @param customerId ID do cliente (opcional)
   * @param period Período (day, month, year)
   * @param partnerId ID do parceiro (opcional)
   * @returns Observable com os dados detalhados do resumo, incluindo listas de contas
   */
  public getSummaryAllItens(
    customerId?: string,
    period: string = 'month',
    partnerId?: string,
    filters?: AccountsDashboardFilter
  ): Observable<SummaryResponse> {

    const url = `${API_PATH}${this.basePath}/summary-all-itens`;

    const payload = this.buildSummaryPayload(period, customerId, partnerId, filters);

    return this.httpClient.post<SummaryResponse>(url, payload);
  }


  private buildSummaryPayload(
    period: string,
    customerId?: string,
    partnerId?: string,
    filters?: AccountsDashboardFilter
  ): any {

    let payload: any = {
      period,
      customerId,
      partnerId,
      ...this.getDateRangeParams(period)
    };

    if (filters) {
      payload = {
        ...payload,
        systemId: filters.systemId,
        status: filters.status,
        type: filters.type,
        startDate: filters.customStartDate,
        endDate: filters.customEndDate,
        month: filters.month,
        page: filters.page,
        limit: filters.limit,
        systemTypes: filters.systemTypes
      };
    }

    return this.cleanObject(payload);
  }


  private getDateRangeParams(period: string): { dateStart?: string; dateEnd?: string } {
    if (period !== 'year') {
      return {};
    }

    const currentYear = new Date().getFullYear();
    return {
      dateStart: new Date(currentYear, 0, 1).toISOString(),
      dateEnd: new Date(currentYear, 11, 31).toISOString()
    };
  }


  private cleanObject(obj: any): any {
    return Object.entries(obj)
      .filter(([_, v]) => v != null)
      .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});
  }

  /**
   * Obtém a distribuição por status das contas
   * @param customerId ID do cliente (opcional)
   * @param period Período (day, month, year)
   * @returns Observable com os dados de distribuição
   */
  public getStatusDistribution(customerId?: string, period: string = 'month'): Observable<any> {
    let url = `${API_PATH}${this.basePath}/status-distribution`;
    const params: any = { period };

    if (customerId) params.customerId = customerId;

    return this.httpClient.get<any>(url, { params });
  }

  /**
 * Gera relatório PDF a partir dos itens selecionados
 * Retorna Blob (application/pdf) para download no front
 */
  public generateReportPdf(payload: any): Observable<Blob> {
    const url = `${API_PATH}${this.basePath}/report/pdf`;

    return this.httpClient.post(url, payload, {
      responseType: 'blob'
    });
  }

  /**
   * Obtém os recebimentos mensais
   * @param period Período (day, month, year)
   * @param customerId ID do cliente (opcional)
   * @param dateStart Data inicial (opcional)
   * @param dateEnd Data final (opcional)
   * @returns Observable com os dados de recebimentos
   */
  public getMonthlyReceivables(
    period: string = 'month',
    customerId?: string,
    dateStart?: string,
    dateEnd?: string
  ): Observable<any> {
    let url = `${API_PATH}${this.basePath}/monthly-receivables`;
    const params: any = { period };

    if (customerId) params.customerId = customerId;
    if (dateStart) params.dateStart = dateStart;
    if (dateEnd) params.dateEnd = dateEnd;

    return this.httpClient.get<any>(url, { params });
  }

  /**
   * Obtém as contas vencidas
   * @param customerId ID do cliente (opcional)
   * @param limit Limite de registros por página
   * @param page Número da página
   * @returns Observable com os dados das contas vencidas
   */
  public getOverdueAccounts(
    customerId?: string,
    limit: number = 10,
    page: number = 1
  ): Observable<any> {
    let url = `${API_PATH}${this.basePath}/overdue-accounts`;
    const params: any = { limit, page };

    if (customerId) params.customerId = customerId;

    return this.httpClient.get<any>(url, { params });
  }

  /**
   * Método alternativo para buscar os dados de adesões mensais
   */
  public getMonthlyAdhesions(): Observable<any> {
    return this.httpClient.get<any>(`${API_PATH}bills/monthly-adhesions`);
  }

  public getClientNotificationTemplates(): Observable<any[]> {
    return this.httpClient.get<any[]>(`${API_PATH}client-notifications/templates`);
  }

  public getClientNotificationTemplatesByType(type: string): Observable<any[]> {
    return this.httpClient.get<any[]>(`${API_PATH}client-notifications/templates/type/${type}`);
  }

  public getClientNotificationTemplatesByTypeAndUser(type: string): Observable<any[]> {
    return this.httpClient.get<any[]>(`${API_PATH}client-notifications/templates/type/${type}/user`);
  }

  public createClientNotification(data: {
    customerId: string;
    billItemId?: string;
    templateId?: string;
    customMessage?: string;
    contactType: string;
  }): Observable<any> {
    return this.httpClient.post<any>(`${API_PATH}client-notifications`, data);
  }

  public createTemplateClientNotification(data: {
    title: string;
    message?: string;
    type?: string;
    isActive?: boolean;
  }): Observable<any> {
    return this.httpClient.post<any>(`${API_PATH}client-notifications/templates`, data);
  }

  /**
   * Busca a próxima parcela a vencer para um cliente
   * @param customerId ID do cliente
   * @returns Observable com a próxima parcela a vencer ou null
   */
  public getCustomerNextUpcomingBillItem(customerId: string): Observable<any> {
    return this.httpClient.get<any>(`${API_PATH}client-notifications/customer/${customerId}/next-upcoming-bill`);
  }

  /**
   * Obtém notificações de um cliente específico
   * @param customerId - ID do cliente
   * @returns Observable com lista de notificações
   */
  public getClientNotifications(customerId: string): Observable<any[]> {
    return this.httpClient.get<any[]>(`${API_PATH}client-notifications/customer/${customerId}`);
  }

  /**
   * Obtém dados sobre os modelos de parcela agrupados por tipo
   * @param customerId ID do cliente (opcional)
   * @param period Período (week, month, year)
   * @param status Status das contas (upcoming, overdue, paid, unpaid)
   * @param type Tipo de parcela (1-Mensalidade, 2-Trimestral, etc)
   * @returns Observable com os dados dos modelos de parcela
   */
  public getParcelModelData(
    customerId?: string,
    period: string = 'month',
    status?: string,
    type?: number
  ): Observable<any[]> {
    let url = `${API_PATH}accounts-dashboard/parcel-model-data`;
    const params: any = { period };

    if (customerId) params.customerId = customerId;
    if (status) params.status = status;
    if (type) params.type = type;

    return this.httpClient.get<any[]>(url, { params });
  }


  public getPlanosPorTipo(
    planType?: string,
    customerId?: string
  ): Observable<any[]> {
    let url = `${API_PATH}${this.basePath}/planos-por-tipo`;
    const params: any = {};

    if (planType && planType !== 'todos') params.planType = planType;
    if (customerId) params.customerId = customerId;

    return this.httpClient.get<any[]>(url, { params });
  }

  public getClientesPorPlano(
    planoId: string
  ): Observable<any[]> {
    let url = `${API_PATH}${this.basePath}/clientes-por-plano/${planoId}`;

    return this.httpClient.get<any[]>(url);
  }

  public getBlockedCustomers(period: string = 'month'): Observable<any[]> {
    let url = `${API_PATH}${this.basePath}/blocked-customers`;
    const params: any = { period };

    return this.httpClient.get<any[]>(url, { params });
  }

  /**
   * Obtém os dados de comissões por tipo de parceiro
   * @param period Período (day, month, year)
   * @returns Observable com os dados de comissões agrupados por tipo de parceiro
   */
  public getComissoesPorParceiro(period: string = 'month'): Observable<any[]> {
    let url = `${API_PATH}${this.basePath}/comissoes-por-parceiro`;
    const params: any = { period };

    return this.httpClient.get<any[]>(url, { params });
  }

  /**
   * Obtém os detalhes das comissões para um tipo específico de parceiro
   * @param tipo Tipo de parceiro (parceiro, consultor, representante, contador, unidade)
   * @returns Observable com os dados detalhados de comissões para o tipo especificado
   */
  public getDetalhesComissoesPorTipo(tipo: string): Observable<any[]> {
    let url = `${API_PATH}${this.basePath}/detalhes-comissoes-por-tipo`;
    const params: any = { tipo };

    return this.httpClient.get<any[]>(url, { params }).pipe(
      map(response => {
        // Se recebemos uma resposta vazia ou sem dados, retornamos um array vazio
        if (!response || !Array.isArray(response) || response.length === 0) {
          return [];
        }

        // Se temos dados, processamos para garantir que os números estejam corretos
        return response.map(item => ({
          ...item,
          value: Number(item.value || 0),
          percentual: Number(item.percentual || 0)
        }));
      }),
      catchError(error => {
        console.error(`Erro ao buscar detalhes de comissões para o tipo ${tipo}:`, error);
        // Mantém o retorno de array vazio para não quebrar a interface,
        // mas o erro agora está logado no console.
        return of([]);
      })
    );
  }

  /**
   * Obtém os totais mensais de vendas e comissões
   * Similar ao método getCustomerMonthlyTotal do serviço de partner
   * @param customerId ID do cliente (opcional)
   * @param partnerId ID do parceiro (opcional)
   * @param period Período (day, month, year)
   * @returns Observable com os dados de totais mensais
   */
  public getCustomerMonthlyTotal(
    customerId?: string,
    partnerId?: string,
    period: string = 'month'
  ): Observable<any> {
    let params: any = { period };
    if (customerId) {
      params.customerId = customerId;
    }
    if (partnerId) {
      params.partnerId = partnerId;
    }

    return this.httpClient.get<any>(`${API_PATH}accounts-dashboard/customer-monthly-total`, { params });
  }

  /**
   * Método centralizado para obter dados de comissão.
   * Este método substitui getComissoesPorParceiro e getDetalhesComissoesPorTipo,
   * para garantir que todos os componentes usem a mesma fonte de dados.
   * @param partnerId ID do parceiro (opcional)
   * @returns Observable com os dados formatados para diferentes visualizações
   */
  public getCommissionData(partnerId?: string): Observable<any> {
    return this.httpClient.get<any>(`${API_PATH}customers/monthly-total`, {
      params: partnerId ? { partnerId } : {}
    }).pipe(
      map(response => {
        // Formatar dados para uso em diferentes componentes
        return {
          totalCarteira: response.totalGeral,
          totalComissao: response.totalComissaoGeral,
          comissoes: {
            parceiro: response.totalComissaoParceiro,
            consultor: response.totalComissaoConsultor,
            representante: this.extractCommissionByType(response.detalhes, 'representante'),
            contador: this.extractCommissionByType(response.detalhes, 'contador'),
            unidade: this.extractCommissionByType(response.detalhes, 'unidade')
          },
          detalhes: response.detalhes
        };
      }),
      catchError(error => {
        console.error('Erro ao carregar dados de comissão unificados:', error);
        return of({
          totalCarteira: 0,
          totalComissao: 0,
          comissoes: {
            parceiro: 0,
            consultor: 0,
            representante: 0,
            contador: 0,
            unidade: 0
          },
          detalhes: {}
        });
      })
    );
  }

  /**
   * Método auxiliar para extrair valores de comissão por tipo dos detalhes
   * @param details Objeto de detalhes da resposta
   * @param type Tipo de comissão a ser extraído (representante, contador, unidade)
   * @returns Valor total para o tipo de comissão
   */
  private extractCommissionByType(details: any, type: string): number {
    if (!details) return 0;

    let total = 0;
    Object.keys(details).forEach(partnerId => {
      const partnerData = details[partnerId];
      partnerData.items?.forEach((item: any) => {
        if (item.detalhamentoComissoes?.[type]?.valor) {
          total += parseFloat(item.detalhamentoComissoes[type].valor);
        }
      });
    });
    return total;
  }

  /**
   * Obtém dados de repasse por tipo de parceiro e status
   * @param period Período (day, week, month, year)
   * @param partnerType Tipo de parceiro (todos, parceiro, consultor, etc)
   * @param status Status do repasse (todos, pendente, pago, cancelado)
   * @returns Observable com os dados de repasse
   */
  public getRepasseData(
    period: string = 'month',
    partnerType: string = 'todos',
    status: string = 'todos'
  ): Observable<any> {
    let url = `${API_PATH}${this.basePath}/repasse-data`;
    const params: any = {
      period,
      partnerType,
      status
    };

    return this.httpClient.get<any>(url, { params });
  }

  /**
   * Obtém detalhes de um repasse específico por ID do parceiro
   * @param repasseId ID do repasse
   * @returns Observable com os dados detalhados do repasse
   */
  public getRepasseDetails(repasseId: string): Observable<any> {
    let url = `${API_PATH}${this.basePath}/repasse-details/${repasseId}`;

    return this.httpClient.get<any>(url);
  }

  /**
   * Obtém dados de comparação entre contas a pagar e repasse
   * @param period Período (week, month, year)
   * @param grouping Agrupamento (day, week, month)
   * @returns Observable com os dados para comparação
   */
  public getComparacaoData(
    period: string = 'month',
    grouping: string = 'month',
    additionalParams?: any
  ): Observable<any> {
    let url = `${API_PATH}${this.basePath}/comparacao-data`;
    const params: any = { period, grouping };

    // Adicionar parâmetros específicos baseados no tipo de período
    if (additionalParams) {
      if (period === 'specific' && additionalParams.month && additionalParams.year) {
        params.month = additionalParams.month;
        params.year = additionalParams.year;
      } else if (period === 'custom' && additionalParams.startDate && additionalParams.endDate) {
        params.startDate = additionalParams.startDate;
        params.endDate = additionalParams.endDate;
      }

      // Adicionar parâmetro de tipo de filtragem
      if (additionalParams.filterType) {
        params.filterType = additionalParams.filterType;
      }
    }

    return this.httpClient.get<any>(url, { params });
  }

  /**
   * Obtém os planos agrupados por sistema
   * @param systemType Tipo de sistema (opcional)
   * @returns Observable com lista de planos agrupados por sistema
   */
  public getPlanosPorSistema(systemType?: string): Observable<any[]> {
    let url = `${API_PATH}${this.basePath}/planos-por-sistema`;
    const params: any = {};

    if (systemType && systemType !== 'todos') params.systemType = systemType;

    return this.httpClient.get<any[]>(url, { params });
  }

  /**
   * Obtém os clientes vinculados a um sistema específico
   * @param systemId ID do sistema
   * @returns Observable com lista de clientes do sistema
   */
  public getClientesPorSistema(systemId: string): Observable<any[]> {
    return this.httpClient.get<any[]>(`${API_PATH}${this.basePath}/clientes-por-sistema/${systemId}`);
  }

  /**
   * Obtém detalhes das parcelas por tipo
   * @param parcelType Tipo de parcela (Mensalidade, Trimestral, etc)
   * @param period Período (week, month, year)
   * @param status Status das parcelas (opcional)
   * @returns Observable com lista de parcelas com detalhes
   */
  public getParcelDetailsByType(
    parcelType: string,
    period: string = 'month',
    status?: string
  ): Observable<any[]> {
    let url = `${API_PATH}${this.basePath}/parcel-details/${encodeURIComponent(parcelType)}`;
    const params: any = { period };

    if (status) params.status = status;

    return this.httpClient.get<any[]>(url, { params });
  }

  /**
   * Obtém os detalhes das parcelas para um tipo específico de modelo
   * @param modelType Tipo de modelo de parcela (Mensalidade, Trimestral, etc)
   * @param customerId ID do cliente (opcional)
   * @param period Período (week, month, year, custom)
   * @param status Status das parcelas (opcional)
   * @param startDate Data inicial para período personalizado (formato YYYY-MM-DD)
   * @param endDate Data final para período personalizado (formato YYYY-MM-DD)
   * @returns Observable com os detalhes das parcelas para o tipo de modelo especificado
   */
  public getParcelDetailsByModelType(
    modelType?: string,
    customerId?: string,
    period: string = 'month',
    status?: string,
    startDate?: string,
    endDate?: string
  ): Observable<{
    modelType: string;
    totalValue: number;
    percentage: number;
    parcels: Array<{
      id: string;
      customerId: string;
      customer: string;
      systemName: string;
      value: number;
      dueDate: string;
      status: string;
      statusCode?: number;
    }>;
  }> {
    let url = `${API_PATH}${this.basePath}/parcel-model-details`;
    const params: any = { period };

    if (modelType) params.modelType = modelType;
    if (customerId) params.customerId = customerId;
    if (status) params.status = status;
    if (period === 'custom' && startDate) params.startDate = startDate;
    if (period === 'custom' && endDate) params.endDate = endDate;

    return this.httpClient.get<any>(url, { params });
  }

  /**
   * Obtém os itens de fatura do tipo anual que já foram pagos
   * @param customerId ID do cliente (opcional)
   * @returns Observable com os itens de fatura anuais pagos
   */
  public getAnnualBills(customerId?: string): Observable<{
    items: Array<{
      billId: string;
      customerId: string;
      clientName: string;
      systemName: string;
      planType: string;
      totalAccountValue: number;
      totalPaidValue: number;
      paymentDate: Date | null;
      renewalDate: Date | null;
      isPaid: boolean;
      dueDate: Date | null;
    }>;
    totalCount: number;
  }> {
    let url = `${API_PATH}${this.basePath}/annual-bills`;
    const params: any = {};

    if (customerId) params.customerId = customerId;

    return this.httpClient.get<any>(url, { params }).pipe(
      map(response => {
        if (response && response.items) {
          response.items.forEach((item: any) => {
            if (item.paymentDate) {
              item.paymentDate = new Date(item.paymentDate);
            }
            if (item.renewalDate) {
              item.renewalDate = new Date(item.renewalDate);
            }
            if (item.dueDate) {
              item.dueDate = new Date(item.dueDate);
            }
          });
        }
        return response;
      })
    );
  }

  public getTotalOpenParcelsValue(
    customerId?: string,
    period: string = 'all_time',
    status?: string,
    startDate?: string,
    endDate?: string
  ): Observable<{ totalValue: number }> {
    let url = `${API_PATH}${this.basePath}/total-open-parcels-value`;
    const params: any = { period };

    if (customerId) params.customerId = customerId;
    if (status) params.status = status;
    if (period === 'custom' && startDate) params.startDate = startDate;
    if (period === 'custom' && endDate) params.endDate = endDate;

    return this.httpClient.get<{ totalValue: number }>(url, { params });
  }

  /**
   * Obtém as parcelas de uma conta específica
   * @param billId ID da conta
   * @returns Observable com os detalhes das parcelas
   */
  public getBillInstallments(billId: string): Observable<BillInstallment[]> {
    const url = `${API_PATH}${this.basePath}/bills/${billId}/installments`;
    return this.httpClient.get<BillInstallment[]>(url);
  }

  /**
   * Busca detalhes de um item de fatura específico pelo ID
   * @param billItemId ID do item de fatura
   * @returns Observable com os detalhes do item de fatura
   */
  public getBillItemById(billItemId: string): Observable<any> {
    return this.httpClient.get<any>(`${API_PATH}bills/bill-items/${billItemId}`);
  }

  /**
   * Busca os clientes vinculados a um parceiro específico
   * @param partnerId ID do parceiro
   * @returns Observable com lista de IDs de clientes do parceiro
   */
  public getCustomersByPartner(partnerId: string): Observable<string[]> {
    // Usar o endpoint de customers com filtro por parceiro
    const filterData = {
      filter: JSON.stringify({
        partner: partnerId  // Usar o ID diretamente, não como objeto
      }),
      limit: '1000', // Limite alto para pegar todos os clientes
      page: '0'  // Página 0 para começar do início
    };

    return this.httpClient.post<any>(`${API_PATH}customers/filter`, filterData).pipe(
      map(response => {
        // A resposta é um array: [customers, total, actives, deactives, locks]
        if (response && Array.isArray(response) && response.length > 0) {
          const customers = response[0]; // Primeiro elemento é a lista de clientes
          if (Array.isArray(customers)) {
            const customerIds = customers.map((customer: any) => customer.id);
            return customerIds;
          }
        }

        return [];
      }),
      catchError(error => {
        return of([]);
      })
    );
  }

  /**
   * Obtém dados filtrados de contas para os cards específicos
   */
  getFilteredBillsData(params: any): Observable<any> {
    const queryParams = new HttpParams()
      .set('type', params.type || 'all')
      .set('customerId', params.customerId || '')
      .set('partnerId', params.partnerId || '')
      .set('startDate', params.startDate || '')
      .set('endDate', params.endDate || '')
      .set('parcelType', params.parcelType || '')
      .set('systemId', params.systemId || '')
      .set('month', params.month || '')
      .set('day', params.day || '')
      .set('state', params.state || '')
      .set('city', params.city || '')
      .set('solutionType', params.solutionType || '')
      .set('partnerType', params.partnerType || '')
      .set('status', params.status || '')
      .set('page', params.page !== undefined ? params.page.toString() : '')
      .set('limit', params.limit !== undefined ? params.limit.toString() : '');

    console.log('🔍 [getFilteredBillsData] Parâmetros enviados:', params);
    console.log('🔍 [getFilteredBillsData] Status enviado:', params.status);

    return this.httpClient.get<any>(`${API_PATH}${this.basePath}/bills-filtered`, { params: queryParams });
  }

  /**
   * Obtém lista de estados brasileiros para filtro de localização
   * @returns Observable com lista de estados
   */
  public getStates(): Observable<Array<{ nome: string; sigla: string }>> {
    return this.httpClient.get<Array<{ nome: string; sigla: string }>>(`${API_PATH}${this.basePath}/location/states`).pipe(
      catchError(error => {
        throw error;
      })
    );
  }

  /**
   * Obtém lista de cidades por estado para filtro de localização
   * @param state Sigla do estado (UF)
   * @returns Observable com lista de cidades
   */
  public getCitiesByState(state: string): Observable<Array<{ nome: string }>> {
    const params = { state };
    return this.httpClient.get<Array<{ nome: string }>>(`${API_PATH}${this.basePath}/location/cities`, { params }).pipe(
      catchError(error => {
        throw error;
      })
    );
  }

  /**
   * Obtém lista de IDs de clientes filtrados por localização
   * @param state Estado para filtrar (opcional)
   * @param city Cidade para filtrar (opcional)
   * @returns Observable com lista de IDs de clientes
   */
  public getCustomersByLocation(state?: string, city?: string): Observable<string[]> {
    const params: any = {};
    if (state) params.state = state;
    if (city) params.city = city;

    return this.httpClient.get<string[]>(`${API_PATH}${this.basePath}/customers-by-location`, { params });
  }

  /**
   * Busca clientes com parcelas vencidas e que NÃO estão bloqueados (para tabela de bloqueio)
   * @param period Período (day, month, year)
   */
  public getCustomersForBlockList(period: string = 'month'): Observable<any[]> {
    let url = `${API_PATH}${this.basePath}/customers-for-block-list`;
    const params: any = { period };
    return this.httpClient.get<any[]>(url, { params });
  }

  /**
   * Obtém dados detalhados de contas a pagar para a aba DRE
   * @param period Período (week, month, year)
   * @returns Observable com dados detalhados de contas a pagar
   */
  public getContasAPagarDetalhadas(period: string = 'month'): Observable<{
    total: number;
    venceHoje: number;
    atrasadas: number;
    pagas: number;
    parcelas: Array<{
      id: string;
      name: string;
      value: number;
      fees: number;
      fine: number;
      dueDate: Date;
      isPaid: boolean;
      paidDate?: Date;
      status: number;
      accountType?: string;
      systemName?: string;
    }>;
  }> {
    let url = `${API_PATH}${this.basePath}/contas-a-pagar-detalhadas`;
    const params: any = { period };

    return this.httpClient.get<any>(url, { params });
  }

  /**
   * Obtém dados detalhados de repasse por status para a aba DRE
   * @param period Período (week, month, year)
   * @returns Observable com dados detalhados de repasse por status
   */
  public getRepasseDetalhadas(period: string = 'month'): Observable<{
    total: number;
    pendente: number;
    pago: number;
    cancelado: number;
    repasses: Array<{
      id: string;
      partnerId: string;
      partnerName: string;
      partnerType: string;
      totalAmount: number;
      settlementDate?: Date;
      createdDate: Date;
      status: 'pendente' | 'pago' | 'cancelado';
      observation?: string;
      city?: string;
      state?: string;
    }>;
  }> {
    let url = `${API_PATH}commission-pass-on/repasse-detalhadas`;
    const params: any = { period };

    return this.httpClient.get<any>(url, { params });
  }

  /**
   * Obtém dados detalhados de contas a receber por status para a aba DRE
   * @param period Período (week, month, year)
   * @returns Observable com dados detalhados de contas a receber por status
   */
  public getContasAReceberDetalhadas(period: string = 'month'): Observable<{
    total: number;
    aReceber: number;
    venceHoje: number;
    atrasadas: number;
    recebidas: number;
    parcelas: Array<{
      id: string;
      customerName: string;
      systemName: string;
      value: number;
      fees: number;
      fine: number;
      discount: number;
      dueDate: Date;
      isPaid: boolean;
      paidDate?: Date;
      status: number;
    }>;
  }> {
    let url = `${API_PATH}${this.basePath}/contas-a-receber-detalhadas`;
    const params: any = { period };

    return this.httpClient.get<any>(url, { params });
  }

  /**
   * Obtém o resumo detalhado das contas a pagar, incluindo dados para modais
   * @param customerId ID do cliente (opcional)
   * @param period Período (day, month, year)
   * @param partnerId ID do parceiro (opcional)
   * @returns Observable com os dados detalhados do resumo de contas a pagar
   */
  public getAccountsPayableSummary(customerId?: string, period: string = 'month', partnerId?: string): Observable<{
    aPagar: number;
    venceHoje: number;
    pago: number;
    vencidos: number;
    totalAberto: number;
    detalhes: {
      aPagar: Array<{
        id: string;
        accountId: string;
        nome: string;
        tipoConta: string;
        nomeSistema: string;
        valor: number;
        vencimento: string;
        dataPagamento?: string;
        status: string;
      }>;
      venceHoje: Array<{
        id: string;
        accountId: string;
        nome: string;
        tipoConta: string;
        nomeSistema: string;
        valor: number;
        vencimento: string;
        dataPagamento?: string;
        status: string;
      }>;
      pagas: Array<{
        id: string;
        accountId: string;
        nome: string;
        tipoConta: string;
        nomeSistema: string;
        valor: number;
        vencimento: string;
        dataPagamento?: string;
        status: string;
      }>;
      vencidas: Array<{
        id: string;
        accountId: string;
        nome: string;
        tipoConta: string;
        nomeSistema: string;
        valor: number;
        vencimento: string;
        dataPagamento?: string;
        status: string;
      }>;
    };
  }> {
    let url = `${API_PATH}${this.basePath}/contas-a-pagar-summary`;
    const params: any = { period };

    if (customerId) params.customerId = customerId;
    if (partnerId) params.partnerId = partnerId;

    return this.httpClient.get<any>(url, { params });
  }
}

// Add interface for bill installments
export interface BillInstallment {
  installmentNumber: number;
  dueDate: string;
  paymentDate?: string;
  value: number;
  paidValue: number;
  status: 'PAID' | 'PENDING' | 'OVERDUE' | 'CANCELED' | 'PARTIAL';
}